import { crudTable, date, index, text } from "@-/dev.improve.haniverse.lib.database.table";

/**
 * User annotations on a sentence — a note and/or a favorite.
 *
 * Deliberately NOT a card. Sentences are context for learning a word, never a study subject, so
 * this never gets an `fsrs_card` and never enters the review queue; memorizing one specific
 * sentence is pointless. Word cards live in `user_word_sentence_card`.
 *
 * Keyed by the sentence TEXT, with no foreign key into the static DB. Two reasons:
 *   1. No actor table references static/content by id — the actor DB references static content
 *      only by opaque stable string (`morpheme_note.morpheme`, `collection_morpheme.morpheme`).
 *   2. Sentences can be added to or removed from the corpus at any time, and static-DB integer
 *      ids are build artifacts assigned by a counter that renumber on every `static.db` rebuild.
 *      Keying on one would silently orphan every note on the next regeneration.
 *
 * Storing the full text also makes the row self-sufficient: it is the input the segmentation
 * fallback re-segments when the sentence is no longer in the corpus and so has no
 * `sentence_word` rows to read.
 *
 * One row per sentence: the row id is derived from the text (`deriveUserSentenceId`), so no unique
 * index is needed, and none may be added (sync cannot resolve a unique conflict).
 */
export const userSentenceTable = crudTable(
  "user_sentence",
  {
    /** Set when the user favorites the sentence; null once un-favorited. */
    favoritedAt: date("favorited_at"),
    /** The sentence text as it was when the user annotated it. Doubles as the join key. */
    sentence: text("sentence").notNull(),
    /** User-authored note about this sentence. */
    text: text("text"),
  },
  (table) => [index("user_sentence_sentence_idx").on(table.sentence)],
);

export type UserSentence = typeof userSentenceTable.$inferSelect;
